import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES, CONTACT } from "../../lib/site";

const ease = [0.22, 1, 0.36, 1];
const lineUp = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, ease, delay: 0.5 + i * 0.14 } }),
};

const HEAD = ["Where Luxury", "Meets Serenity."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.28, 0.55]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden">
      {/* Parallax image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={IMAGES.villaExterior} alt="Mediterranean villa overlooking the ocean" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ocean-teal" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-charcoal/20" />

      {/* Right-side readability scrim */}
      <div className="absolute inset-0 bg-gradient-to-l from-charcoal/55 via-charcoal/20 to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end md:justify-center items-start md:items-end text-left md:text-right pb-24 md:pb-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <span className="text-xs font-sans font-semibold tracking-[0.32em] uppercase text-warm-white/90">
            Boutique Cleaning &amp; Estate Care — Australia
          </span>
        </motion.div>

        <h1 className="mt-6 text-warm-white font-serif font-light tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.02]">
          {HEAD.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span variants={lineUp} custom={i} initial="hidden" animate="show" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease }}
          className="mt-8 max-w-xl md:ml-auto text-warm-white/90 font-sans font-light text-base md:text-lg leading-relaxed"
        >
          Inspired by Mediterranean living, Casa Galênê Cleaning delivers boutique residential,
          commercial and post-construction cleaning with timeless attention to detail.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease }}
          className="mt-4 max-w-md md:ml-auto text-warm-white/70 font-sans font-light text-sm md:text-base leading-relaxed"
        >
          Serving Jervis Bay, Sydney, Adelaide, Townsville &amp; Airlie Beach — every space cared for
          with five-star, hospitality-inspired precision.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease }}
          className="mt-10 flex flex-wrap items-center gap-4 justify-start md:justify-end"
        >
          <a
            href="#quote"
            data-testid="hero-book-btn"
            className="rounded-full bg-warm-white px-8 py-4 text-sm font-medium tracking-wide text-ocean-teal hover:bg-antique-gold hover:text-warm-white transition-colors"
          >
            Book Your Service
          </a>
          <a
            href="#quote"
            data-testid="hero-quote-btn"
            className="rounded-full border border-warm-white/60 px-8 py-4 text-sm font-medium tracking-wide text-warm-white hover:bg-warm-white/10 transition-colors"
          >
            Request a Quote
          </a>
        </motion.div>
      </div>

      {/* scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute z-10 bottom-8 right-6 md:right-10 hidden sm:flex items-center gap-3 text-warm-white/70"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="h-10 w-px bg-warm-white/40 overflow-hidden relative">
          <motion.span
            className="absolute inset-x-0 top-0 h-4 bg-antique-gold"
            animate={{ y: [-16, 40] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
